"use client";
import {
  Copy,
  ListFilter,
  LockKeyhole,
  Network,
  Radar,
  Search,
} from "lucide-react";
import {
  useState,
} from "react";
import {
  type Indicator,
  type ScanReport,
} from "../../../lib/types";
import {
  maskSecret,
} from "../../shared/io";
import {
  IconButton,
} from "../../shared/primitives";


export function IndicatorsView({
  report,
  revealSecrets,
  onCopy,
}: {
  report: ScanReport;
  revealSecrets: boolean;
  onCopy: (value: string, message?: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [type, setType] = useState<Indicator["type"] | "all">("all");
  const types = Array.from(new Set(report.indicators.map((item) => item.type)));
  const indicators = report.indicators.filter(
    (indicator) =>
      (type === "all" || indicator.type === type) &&
      (!query ||
        `${indicator.value} ${indicator.entry}`
          .toLowerCase()
          .includes(query.toLowerCase())),
  );

  return (
    <div className="view-stack">
      <section className="metrics-row">
        <div>
          <span className="metric-icon">
            <Network size={18} />
          </span>
          <span>
            <small>Total indicators</small>
            <strong>{report.indicators.length}</strong>
          </span>
        </div>
        <div>
          <span className="metric-icon danger">
            <LockKeyhole size={18} />
          </span>
          <span>
            <small>High-risk secrets</small>
            <strong>
              {
                report.indicators.filter((indicator) => indicator.risk === "high")
                  .length
              }
            </strong>
          </span>
        </div>
        <div>
          <span className="metric-icon">
            <Radar size={18} />
          </span>
          <span>
            <small>Unique types</small>
            <strong>{types.length}</strong>
          </span>
        </div>
      </section>
      <section className="panel table-panel">
        <div className="table-toolbar">
          <div className="search-field">
            <Search size={15} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search value or source"
              aria-label="Search indicators"
            />
          </div>
          <label className="select-control">
            <ListFilter size={14} />
            <select
              value={type}
              onChange={(event) =>
                setType(event.target.value as Indicator["type"] | "all")
              }
              aria-label="Indicator type"
            >
              <option value="all">All types</option>
              {types.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="data-table indicator-table">
          <div className="data-table-head">
            <span>Risk</span>
            <span>Type</span>
            <span>Indicator</span>
            <span>Source</span>
            <span />
          </div>
          {indicators.map((indicator) => (
            <div className="data-table-row" key={indicator.id}>
              <span>
                <span className={`risk-pill risk-${indicator.risk}`}>
                  {indicator.risk}
                </span>
              </span>
              <span className="mono-cell">{indicator.type}</span>
              <code title={indicator.value}>
                {maskSecret(indicator, revealSecrets)}
              </code>
              <span className="path-line" title={indicator.entry}>
                {indicator.entry}
              </span>
              <IconButton
                label="Copy indicator"
                onClick={() => onCopy(indicator.value, "Indicator copied")}
              >
                <Copy size={14} />
              </IconButton>
            </div>
          ))}
        </div>
        {!indicators.length && (
          <div className="empty-panel">
            <Radar size={26} />
            <strong>No indicators to display</strong>
            <span>Static strings did not expose matching infrastructure.</span>
          </div>
        )}
      </section>
    </div>
  );
}
